import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Modal, message } from "antd";
import { AgTableClient } from "app/shared/AgTable";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import Loader from "app/shared/Loader";
import Uploader from "app/shared/Uploader";
import Typography from "app/shared/Typography";
import { colorPicker } from "app/utils/color.helper";
import { appMetaConfig } from "global.constants";

const API_BASE_UPLOAD_DATA = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/uploadData";
const API_UPLOAD_INVOICE = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice/upload/single";
const API_DELETE_INVOICE = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice/upload/delete";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

function InvoiceButton() {
  const { trackingId } = useParams<{ trackingId: string }>();
  const [rowData, setRowData] = useState<any[]>([]);
  const [alreadyUploaded, setAlreadyUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState<{ visible: boolean; rowData: any }>({ visible: false, rowData: null });
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFilePreview, setSelectedFilePreview] = useState<string | null>(null);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkFiles, setBulkFiles] = useState<any[]>([]);
  const [showThankYou, setShowThankYou] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const hasFetched = useRef(false);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_BASE_UPLOAD_DATA}/${trackingId}`);
      const response = await res.json();
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      const allUploaded =
        data.length > 0 &&
        data.every(
          (r: any) => Array.isArray(r.uploadLinks) && r.uploadLinks.length > 0
        );
      if (allUploaded) {
        setAlreadyUploaded(true);
      } else {
        setRowData(data);
      }
    } catch (error) {
      setRowData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, []);

  const closeModal = () => {
    const wasUploaded = uploaded;
    setUploadModal({ visible: false, rowData: null });
    setUploaded(false);
    setSelectedFile(null);
    setSelectedFilePreview(null);
    if (wasUploaded) fetchData();
  };

  const handleFileUpload = (file: any) => {
    const originFile = file.originFileObj || file;
    if (originFile.size > MAX_FILE_SIZE) {
      messageApi.error("File size exceeds the 50MB limit. Please upload a smaller file.");
      return;
    }
    const preview = originFile.type?.startsWith("image/") ? URL.createObjectURL(originFile) : null;
    setSelectedFile(originFile);
    setSelectedFilePreview(preview);
  };

  const handleSubmitUpload = async () => {
    if (!selectedFile) return;
    const row = uploadModal.rowData;
    const ext = selectedFile.name.split(".").pop();
    const filename = `${row?.email_bookingId || "booking"}_${Date.now()}.${ext}`;
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("clientId", row?.booking_client_id || "");
    formData.append("email_sharableBookingId", row?.email_sharableBookingId || "");
    formData.append("bucket_name", "fink-followup-data");
    formData.append("trackingId", trackingId || "");
    formData.append("filename", filename);

    try {
      setUploading(true);
      const res = await fetch(API_UPLOAD_INVOICE, { method: "POST", body: formData });
      const result = await res.json();
      if (res.ok || result?.status) {
        setUploaded(true);
        setSelectedFile(null);
        setSelectedFilePreview(null);
      } else {
        messageApi.error(result?.message || "Failed to upload invoice.");
      }
    } catch {
      messageApi.error("Failed to upload invoice.");
    } finally {
      setUploading(false);
    }
  };

  const handleBulkFileUpload = (file: any) => {
    const originFile = file.originFileObj || file;
    if (originFile.size > MAX_FILE_SIZE) {
      messageApi.error(`"${originFile.name}" exceeds the 50MB limit and was not added.`);
      return;
    }
    const preview = originFile.type?.startsWith("image/")
      ? URL.createObjectURL(originFile)
      : null;
    setBulkFiles((prev) => [...prev, { file: originFile, name: originFile.name, preview }]);
  };

  const removeBulkFile = (index: number) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkUpload = async () => {
    const formData = new FormData();
    const ts = Date.now();
    bulkFiles.forEach((item, i) => {
      const ext = item.file.name.split(".").pop();
      formData.append("files", item.file);
      formData.append("filename", `bulk_${ts}_${i}.${ext}`);
    });
    formData.append("clientId", rowData[0]?.booking_client_id || "");
    formData.append("trackingId", trackingId || "");
    formData.append("bucket_name", "fink-followup-data");
    formData.append("uploadRemarks", bulkFiles.length < rowData.length ? "partial upload" : "complete");

    try {
      setBulkUploading(true);
      const res = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice/upload/bulk",
        {
          method: "POST",
          body: formData,
        }
      );
      const result = await res.json();
      if (res.ok || result?.status) {
        setBulkModal(false);
        setBulkConfirm(false);
        setBulkFiles([]);
        setShowThankYou(true);
      } else {
        messageApi.error(result?.message || "Failed to upload invoices.");
      }
    } catch {
      messageApi.error("Failed to upload invoices.");
    } finally {
      setBulkUploading(false);
    }
  };

  const columnDefs = useMemo(() => [
    {
      headerName: "Booking ID",
      field: "email_bookingId",
      filter: "agTextColumnFilter",
      minWidth: 140,
    },
    {
      headerName: "Sharable Booking ID",
      field: "email_sharableBookingId",
      filter: "agTextColumnFilter",
      cellDataType: "text",
      minWidth: 180,
    },
    {
      headerName: "Hotel Name",
      field: "email_hotel_name",
      filter: "agTextColumnFilter",
      minWidth: 220,
    },
    {
      headerName: "Hotel City",
      field: "email_hotel_city",
      filter: "agTextColumnFilter",
      minWidth: 140,
    },
    {
      headerName: "Hotel State",
      field: "email_hotel_state",
      filter: "agTextColumnFilter",
      minWidth: 130,
    },
    {
      headerName: "Hotel GSTIN",
      field: "email_hotel_gstin",
      filter: "agTextColumnFilter",
      minWidth: 180,
    },
    {
      headerName: "Traveller",
      field: "email_travellers_info",
      filter: "agTextColumnFilter",
      minWidth: 180,
      valueGetter: (params: any) => {
        const info = params.data?.email_travellers_info;
        return Array.isArray(info) ? info.map((t: any) => t.name).join(", ") : "";
      },
    },
    {
      headerName: "Check-in Date",
      field: "email_checkindate",
      filter: "agDateColumnFilter",
      minWidth: 160,
      valueFormatter: (params: any) =>
        params.value ? new Date(params.value).toLocaleDateString() : "—",
    },
    {
      headerName: "Check-out Date",
      field: "email_checkoutdate",
      filter: "agDateColumnFilter",
      minWidth: 160,
      valueFormatter: (params: any) =>
        params.value ? new Date(params.value).toLocaleDateString() : "—",
    },
    {
      headerName: "Booking Date",
      field: "email_bookingdate",
      filter: "agDateColumnFilter",
      minWidth: 160,
      valueFormatter: (params: any) =>
        params.value ? new Date(params.value).toLocaleDateString() : "—",
    },
    {
      headerName: "Contact Email",
      field: "email_hotel_contact_email",
      filter: "agTextColumnFilter",
      minWidth: 220,
      valueGetter: (params: any) => {
        const emails = params.data?.email_hotel_contact_email;
        return Array.isArray(emails) ? emails.join(", ") : "";
      },
    },
    {
      headerName: "Contact Phone",
      field: "email_hotel_contact_phone",
      filter: "agTextColumnFilter",
      minWidth: 180,
      valueGetter: (params: any) => {
        const phones = params.data?.email_hotel_contact_phone;
        return Array.isArray(phones) ? phones.join(", ") : "";
      },
    },
    {
      headerName: "Client ID",
      field: "booking_client_id",
      filter: "agTextColumnFilter",
      minWidth: 180,
    },
    {
      headerName: "Mail Status",
      field: "mailConfigStauts",
      filter: "agTextColumnFilter",
      minWidth: 140,
    },
    {
      headerName: "Last Mail Sent",
      field: "last_mail_sent_at",
      filter: "agDateColumnFilter",
      minWidth: 180,
      valueFormatter: (params: any) =>
        params.value ? new Date(params.value).toLocaleString() : "—",
    },
    {
      headerName: "Upload",
      field: "_id",
      minWidth: 120,
      pinned: "right" as const,
      cellRenderer: (params: any) => {
        const done = params.data?.uploadSingleStatus === true;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Button
              type="primary"
              size="small"
              icon={<UploadOutlined />}
              disabled={done}
              onClick={() => {
                setUploaded(false);
                setUploadModal({ visible: true, rowData: params.data });
              }}
            >
              {done ? "Uploaded" : "Upload"}
            </Button>
            {done && (
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={async () => {
                  try {
                    const res = await fetch(API_DELETE_INVOICE, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ _id: params.data._id, uploadLinks: params.data.uploadLinks }),
                    });
                    const result = await res.json();
                    if (res.ok || result?.status) {
                      const updated = { ...params.data, uploadSingleStatus: false, uploadLinks: [] };
                      params.node.setData(updated);
                      setRowData((prev) =>
                        prev.map((r) => (r._id === params.data._id ? updated : r))
                      );
                    } else {
                      messageApi.error(result?.message || "Failed to delete invoice.");
                    }
                  } catch {
                    messageApi.error("Failed to delete invoice.");
                  }
                }}
              />
            )}
          </div>
        );
      },
    },
  ], []);

  useEffect(() => {
    if (!showThankYou) return;
    window.history.pushState(null, "", window.location.href);
    const handler = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [showThankYou]);

  if (isLoading) {
    return (
      <div style={{ height: "100vh", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f6fa" }}>
        {contextHolder}
        <Loader />
      </div>
    );
  }

  if (alreadyUploaded) {
    return (
      <div style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f5f6fa", gap: 16 }}>
        <Typography variant="h4" style={{ fontWeight: 700 }}>✅ All Invoices Uploaded</Typography>
        <Typography variant="body1" style={{ color: colorPicker("neutral.700"), textAlign: "center", maxWidth: 400 }}>
          All invoices for this booking have already been uploaded. No further action is needed.
        </Typography>
        <img src={appMetaConfig?.app?.logo} style={{ marginTop: 24, width: 120 }} />
      </div>
    );
  }

  if (showThankYou) {
    return (
      <div style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f5f6fa", gap: 16 }}>
        <Typography variant="h4" style={{ fontWeight: 700 }}>🎉 Thank you!</Typography>
        <Typography variant="body1" style={{ color: colorPicker("neutral.700"), textAlign: "center", maxWidth: 400 }}>
          Your invoice{bulkFiles.length > 1 ? "s have" : " has"} been uploaded successfully. You can close this window now.
        </Typography>
        <img src={appMetaConfig?.app?.logo} style={{ marginTop: 24, width: 120 }} />
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column", background: "#f5f6fa" }}>
      {contextHolder}

      {/* Custom header */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e8eaed",
        padding: "0 24px",
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" style={{ fontWeight: 600, lineHeight: 1.2 }}>
            {rowData[0]?.email_hotel_name || "—"}
          </Typography>
          <Typography variant="xs" style={{ color: colorPicker("neutral.700") }}>
            GSTIN: <b style={{ color: colorPicker("neutral.900") }}>{rowData[0]?.email_hotel_gstin || "—"}</b>
          </Typography>
        </div>
        <Button type="primary" size="middle" icon={<UploadOutlined />} style={{ padding: "0 20px" }} onClick={() => setBulkModal(true)}>
          Bulk Upload
        </Button>
      </div>

      {/* Bulk Upload Modal */}
      <Modal
        open={bulkModal}
        onCancel={() => { setBulkModal(false); setBulkFiles([]); }}
        footer={null}
        width={640}
        centered
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 8px 8px" }}>
          <Typography variant="h6" style={{ marginBottom: 12, fontWeight: 700, alignSelf: "flex-start" }}>
            Bulk Upload Invoices
          </Typography>
          <Typography
            variant="caption"
            style={{ color: colorPicker("neutral.700"), marginBottom: 24, textAlign: "center" }}
          >
            Upload multiple invoices at once for{" "}
            <b style={{ color: colorPicker("primary.700") }}>
              {rowData[0]?.email_hotel_name || "—"}
            </b>
          </Typography>

          <div style={{ width: "100%" }}>
            {bulkUploading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <Loader />
              </div>
            ) : (
              <>
                <Uploader
                  multiple={true}
                  accept=".pdf,.jpg,.jpeg,.png"
                  fileType={
                    <Typography variant="xs" style={{ textAlign: "center", color: colorPicker("neutral.700") }}>
                      PDF/Image Only — max 50MB per file, <b>* select multiple files</b>
                    </Typography>
                  }
                  onSelect={handleBulkFileUpload}
                />
                {bulkFiles.length > 0 && (
                  <>
                    {/* Selected files list */}
                    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto" }}>
                      {bulkFiles.map((item, index) => (
                        <div key={index} style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "6px 12px", borderRadius: 8,
                          border: `1px solid ${colorPicker("neutral.300")}`,
                          background: "#fafafa",
                        }}>
                          {item.preview ? (
                            <img src={item.preview} alt={item.name} style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
                          ) : (
                            <div style={{
                              width: 32, height: 32, borderRadius: 4, background: colorPicker("neutral.200"),
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 10, fontWeight: 600, color: colorPicker("neutral.700"), flexShrink: 0,
                            }}>PDF</div>
                          )}
                          <Typography variant="xs" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.name}
                          </Typography>
                          <Button type="text" size="small" danger onClick={() => removeBulkFile(index)}>
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>

                    <Button
                      type="primary"
                      style={{ marginTop: 16, width: "100%" }}
                      onClick={() => setBulkConfirm(true)}
                    >
                      Upload All ({bulkFiles.length} File{bulkFiles.length !== 1 ? "s" : ""})
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          <img src={appMetaConfig?.app?.logo} style={{ marginTop: 24, width: 120 }} />
        </div>
      </Modal>

      {/* Bulk upload confirmation modal */}
      <Modal
        open={bulkConfirm}
        footer={null}
        closable={false}
        centered
        width={420}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 8px 8px", gap: 12, textAlign: "center" }}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>Confirm Upload</Typography>
          <Typography variant="body1" style={{ color: colorPicker("neutral.700") }}>
            You are uploading{" "}
            <b style={{ color: colorPicker("primary.700") }}>{bulkFiles.length} file{bulkFiles.length !== 1 ? "s" : ""}</b>{" "}
            out of{" "}
            <b>{rowData.length} total booking{rowData.length !== 1 ? "s" : ""}</b>.
          </Typography>
          <Typography variant="xs" style={{ color: colorPicker("neutral.600") }}>
            Are you sure you want to proceed?
          </Typography>
          <div style={{ display: "flex", gap: 8, width: "100%", marginTop: 8 }}>
            <Button style={{ flex: 1 }} onClick={() => setBulkConfirm(false)}>
              Go Back
            </Button>
            <Button type="primary" style={{ flex: 1 }} loading={bulkUploading} onClick={handleBulkUpload}>
              Yes, Upload
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={uploadModal.visible}
        onCancel={closeModal}
        footer={null}
        width={640}
        centered
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 8px 8px" }}>
          <Typography variant="h6" style={{ marginBottom: 12, fontWeight: 700, alignSelf: "flex-start" }}>
            Upload Invoice
          </Typography>
          <Typography
            variant="caption"
            style={{ color: colorPicker("neutral.700"), marginBottom: 24, textAlign: "center" }}
          >
            Please upload invoice for booking made with reference to booking
            confirmation/ TRF ID -{" "}
            <b style={{ color: colorPicker("primary.700") }}>
              {uploadModal.rowData?.email_sharableBookingId || "—"}
            </b>
          </Typography>

          <div style={{ width: "100%" }}>
            {uploading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <Loader />
              </div>
            ) : uploaded ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 32, gap: 8 }}>
                <Typography variant="h5">🎉 Thanks for uploading invoice</Typography>
                <Typography variant="xs" style={{ color: colorPicker("neutral.800") }}>
                  you can close this window now
                </Typography>
              </div>
            ) : selectedFile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: `1px solid ${colorPicker("neutral.300")}`, borderRadius: 8, background: "#fafafa" }}>
                  {selectedFilePreview ? (
                    <img src={selectedFilePreview} alt={selectedFile.name} style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 6, background: colorPicker("neutral.200"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: colorPicker("neutral.700"), flexShrink: 0 }}>PDF</div>
                  )}
                  <Typography variant="xs" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedFile.name}
                  </Typography>
                  <label style={{ cursor: "pointer" }}>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileUpload(f);
                        e.target.value = "";
                      }}
                    />
                    <Button type="text" size="small" style={{ color: colorPicker("primary.700") }} onClick={(e) => { e.currentTarget.closest("label")?.click(); }}>
                      Reupload
                    </Button>
                  </label>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => { setSelectedFile(null); setSelectedFilePreview(null); }} />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button type="primary" onClick={handleSubmitUpload}>
                    Finish
                  </Button>
                </div>
              </div>
            ) : (
              <Uploader
                multiple={false}
                accept=".pdf,.jpg,.jpeg,.png"
                fileType={
                  <Typography variant="xs" style={{ textAlign: "center", color: colorPicker("neutral.700") }}>
                    PDF/Image Only — max 50MB per file
                  </Typography>
                }
                onSelect={handleFileUpload}
              />
            )}
          </div>

          {!selectedFile && <img src={appMetaConfig?.app?.logo} style={{ marginTop: 24, width: 120 }} />}
        </div>
      </Modal>

      <div style={{ flex: 1, minHeight: 0, padding: "16px 24px" }}>
        {/* @ts-ignore */}
        <AgTableClient rowData={rowData} columnDefs={columnDefs} />
      </div>
    </div>
  );
}

export default InvoiceButton;
